// Spec 48 ai-ux — confidence_badge.
//
// "Three-state chip conveying assistant certainty. Confident (explicit
// sources), Best guess (inferred), Not sure (insufficient data — offers
// next step). Wording aligns with content-voice voice pillars." (spec
// 48 line 832)
//
// a11y per spec 48:
//   - Badge is role='status' with aria-label='Confidence: {label}'
//   - Tooltip is keyboard-focusable via space; screen reader receives
//     aria-describedby
//   - Colors: Confident → state_success; Best guess → state_info; Not
//     sure → state_warning — each paired with a distinct icon
//   - Icon + label + color — never color alone

import { forwardRef, useId, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type ConfidenceState = "confident" | "best_guess" | "not_sure";

export interface ConfidenceBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  readonly state: ConfidenceState;
  /** Decorative state-specific icon (renders aria-hidden). */
  readonly icon?: ReactNode;
  /** Optional explanation rendered as aria-describedby reference text. */
  readonly explanation?: string;
  /** Only when state="not_sure" — onClick invokes "Ask me to clarify" CTA. */
  readonly onClarifyClick?: () => void;
  /** Override label for "Confident" (i18n). */
  readonly confidentLabel?: string;
  /** Override label for "Best guess". */
  readonly bestGuessLabel?: string;
  /** Override label for "Not sure". */
  readonly notSureLabel?: string;
  /** Override CTA label for the not_sure state. */
  readonly clarifyCtaLabel?: string;
}

export const ConfidenceBadge = forwardRef<HTMLSpanElement, ConfidenceBadgeProps>(
  (
    {
      state,
      icon,
      explanation,
      onClarifyClick,
      confidentLabel = "Confident",
      bestGuessLabel = "Best guess",
      notSureLabel = "Not sure",
      clarifyCtaLabel = "Ask me to clarify",
      className,
      ...rest
    },
    ref,
  ) => {
    const explanationId = useId();
    const label =
      state === "confident"
        ? confidentLabel
        : state === "best_guess"
          ? bestGuessLabel
          : notSureLabel;

    return (
      <span
        ref={ref}
        role="status"
        aria-label={`Confidence: ${label}`}
        aria-describedby={explanation !== undefined ? explanationId : undefined}
        data-component="confidence-badge"
        data-state={state}
        className={cx(
          "nai-confidence-badge",
          `nai-confidence-badge--${state}`,
          className,
        )}
        {...rest}
      >
        {icon !== undefined && (
          <span
            aria-hidden="true"
            data-component="confidence-badge-icon"
            className="nai-confidence-badge-icon"
          >
            {icon}
          </span>
        )}
        <span data-component="confidence-badge-label" className="nai-confidence-badge-label">
          {label}
        </span>
        {explanation !== undefined && (
          <span
            id={explanationId}
            data-component="confidence-badge-explanation"
            className="nai-confidence-badge-explanation"
          >
            {explanation}
          </span>
        )}
        {state === "not_sure" && onClarifyClick !== undefined && (
          <button
            type="button"
            data-component="confidence-badge-clarify-cta"
            className="nai-confidence-badge-clarify-cta"
            onClick={onClarifyClick}
          >
            {clarifyCtaLabel}
          </button>
        )}
      </span>
    );
  },
);
ConfidenceBadge.displayName = "ConfidenceBadge";
